import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import './CryptoTrades.css';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore } from 'firebase/firestore';
import ignisLogo from '../../assets/images/logo-concept-2-chart-flame.svg';

const functions = getFunctions();
const db = getFirestore();

// Cloud Function references
const submitBotApiKeysFn = httpsCallable(functions, 'submitBotApiKeys');
const getBotStatusFn = httpsCallable(functions, 'getBotStatus');
const updateBotApiKeysFn = httpsCallable(functions, 'updateBotApiKeys');
const getBotInfoFn = httpsCallable(functions, 'getBotInfo');
const verifyBotPaymentFn = httpsCallable(functions, 'verifyBotPayment');

// 🎰 SLOT LIMIT — Max users accepted into Ignis
const MAX_SLOTS = 20;

// Tier config
const TIERS = [
    { id: 'bronze', label: 'Bronze', price: 500, cap: '$2,000', color: '#CD7F32' },
    { id: 'silver', label: 'Silver', price: 1000, cap: '$5,000', color: '#C0C0C0' },
    { id: 'gold',   label: 'Gold',   price: 2000, cap: '$10,000', color: '#FFD700' },
];

// 🔒 Your USDT wallet address — UPDATE THIS
const PAYMENT_WALLET = "TWRDcCK5XozazzfpbQDGvhkhYTvw3BrhHL";
const PAYMENT_NETWORK = "TRC-20 (Tron)";

// 📱 Telegram group invite link — UPDATE THIS
const TELEGRAM_GROUP_LINK = "https://t.me/+hSDOOOwDkAEwMzdk";

// 🔒 ENROLLMENT LOCK — Flip to true on February 27th launch day
// ENROLLMENT_OPEN is now controlled remotely via Firestore config/bot.enrollmentEnabled
// Toggle it from your admin panel or Firebase console — no redeploy needed

const CryptoTrades = () => {
    const navigate = useNavigate();
    const { user, profile, loading } = useAuth();

    // Bot status
    const [botStatus, setBotStatus] = useState(null);
    const [statusLoading, setStatusLoading] = useState(true);

    // Enrollment form
    const [selectedTier, setSelectedTier] = useState('bronze');
    const [selectedExchange, setSelectedExchange] = useState('bybit');
    const [tradingCapital, setTradingCapital] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [apiSecret, setApiSecret] = useState('');
    const [showSecret, setShowSecret] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [submitSuccess, setSubmitSuccess] = useState('');

    // Key update
    const [editingKeys, setEditingKeys] = useState(false);
    const [newApiKey, setNewApiKey] = useState('');
    const [newApiSecret, setNewApiSecret] = useState('');
    const [updateError, setUpdateError] = useState('');
    const [updateSuccess, setUpdateSuccess] = useState('');
    const [updating, setUpdating] = useState(false);

    // Tab state for enrolled users
    const [dashTab, setDashTab] = useState('overview');

    // TX hash verification
    const [txHash, setTxHash] = useState('');
    const [verifying, setVerifying] = useState(false);
    const [verifyError, setVerifyError] = useState('');
    const [verifySuccess, setVerifySuccess] = useState('');

    // Slot tracking
    const [slotsTaken, setSlotsTaken] = useState(0);
    const slotsRemaining = MAX_SLOTS - slotsTaken;
    const slotsFull = slotsRemaining <= 0;
    const [enrollmentEnabled, setEnrollmentEnabled] = useState(true);
    const [maintenanceMsg, setMaintenanceMsg] = useState('');

    // Fetch active bot user count + enrollment toggle
    const fetchSlots = useCallback(async () => {
        try {
            const result = await getBotInfoFn();
            setSlotsTaken(result.data.slotsTaken || 0);
            setEnrollmentEnabled(result.data.enrollmentEnabled !== false);
            setMaintenanceMsg(result.data.maintenanceMessage || '');
        } catch (err) {
            console.error('Error fetching slot count:', err);
        }
    }, []);

    // Fetch bot status
    const fetchStatus = useCallback(async () => {
        if (!user) return;
        try {
            setStatusLoading(true);
            const result = await getBotStatusFn();
            setBotStatus(result.data);
        } catch (err) {
            console.error('Error fetching bot status:', err);
        } finally {
            setStatusLoading(false);
        }
    }, [user]);

    useEffect(() => {
        fetchSlots();
        if (!loading && user) {
            fetchStatus();
        }
    }, [loading, user, fetchStatus, fetchSlots]);

    // Submit API keys
    const handleSubmitKeys = async (e) => {
        e.preventDefault();
        setSubmitError('');
        setSubmitSuccess('');

        if (!apiKey.trim() || !apiSecret.trim()) {
            setSubmitError('Please enter both your API key and secret.');
            return;
        }

        const capitalNum = parseFloat(tradingCapital);
        const tierCap = TIERS.find(t => t.id === selectedTier)?.cap?.replace(/[^0-9]/g, '') || 2000;
        if (!tradingCapital || isNaN(capitalNum) || capitalNum < 100) {
            setSubmitError('Please enter your trading capital (minimum $100).');
            return;
        }
        if (capitalNum > Number(tierCap)) {
            setSubmitError(`Trading capital cannot exceed ${TIERS.find(t => t.id === selectedTier)?.cap} for the ${TIERS.find(t => t.id === selectedTier)?.label} tier.`);
            return;
        }

        try {
            setSubmitting(true);
            const result = await submitBotApiKeysFn({
                apiKey: apiKey.trim(),
                apiSecret: apiSecret.trim(),
                tier: selectedTier,
                tradingCapital: capitalNum,
                exchange: selectedExchange,
            });
            setSubmitSuccess(result.data.message);
            setApiKey('');
            setApiSecret('');
            setTradingCapital('');
            // Refresh status
            await fetchStatus();
        } catch (err) {
            setSubmitError(err.message || 'Failed to submit API keys.');
        } finally {
            setSubmitting(false);
        }
    };

    // Update API keys
    const handleUpdateKeys = async () => {
        setUpdateError('');
        setUpdateSuccess('');

        if (!newApiKey.trim() || !newApiSecret.trim()) {
            setUpdateError('Both fields are required.');
            return;
        }

        try {
            setUpdating(true);
            await updateBotApiKeysFn({
                apiKey: newApiKey.trim(),
                apiSecret: newApiSecret.trim(),
                exchange: botStatus?.exchange || 'bybit',
            });
            setUpdateSuccess('API keys updated successfully.');
            setNewApiKey('');
            setNewApiSecret('');
            setEditingKeys(false);
        } catch (err) {
            setUpdateError(err.message || 'Failed to update keys.');
        } finally {
            setUpdating(false);
        }
    };

    // ── VERIFY PAYMENT VIA BLOCKCHAIN ──
    const handleVerifyPayment = async () => {
        setVerifyError('');
        setVerifySuccess('');

        const cleaned = txHash.trim();
        if (!cleaned) {
            setVerifyError('Please paste your transaction hash.');
            return;
        }

        if (cleaned.length !== 64) {
            setVerifyError('Transaction hash should be 64 characters. Please check and try again.');
            return;
        }

        try {
            setVerifying(true);
            const result = await verifyBotPaymentFn({ txHash: cleaned });
            const data = result.data;

            if (data.success) {
                setVerifySuccess(data.message || 'Payment verified! Your subscription is now active.');
                setTxHash('');
                // Refresh bot status to show active dashboard
                setTimeout(() => {
                    fetchStatus();
                }, 1500);
            }
        } catch (err) {
            const msg = err.message || 'Verification failed. Please try again.';
            setVerifyError(msg);
        } finally {
            setVerifying(false);
        }
    };

    // Loading state
    if (loading) {
        return (
            <section className="ct">
                <div className="ct__container">
                    <p className="ct__empty">Loading...</p>
                </div>
            </section>
        );
    }

    // Not signed in
    if (!user) {
        return (
            <section className="ct">
                <div className="ct__container">
                    <div className="ct__hero">
                        <img src={ignisLogo} alt="Ignis" className="ct__hero-img" />
                        <h1 className="ct__title">
                            Meet <span className="ct__title-accent">Ignis</span>
                        </h1>
                        <p className="ct__subtitle">
                            Your AI-powered crypto trading engine. Ignis connects to your exchange and trades 24/7 — 
                            <span className="ct__subtitle-hl">trend detection</span>, 
                            <span className="ct__subtitle-hl">precision entries</span>, and 
                            <span className="ct__subtitle-hl">multi-tier take profits</span>.
                        </p>
                        <button className="ct__cta-btn" onClick={() => navigate('/signin')}>
                            Sign In to Get Started
                        </button>
                    </div>
                </div>
            </section>
        );
    }

    // ==========================================
    // NOT ENROLLED, EXPIRED, or DEACTIVATED — Show enrollment form
    // ==========================================
    const isExpired = botStatus?.isExpired === true;
    const isDeactivated = botStatus?.enrolled && botStatus?.hasKeys && !botStatus?.active && !botStatus?.pendingActivation;
    if (!botStatus?.enrolled || !botStatus?.hasKeys || isExpired || isDeactivated) {
        return (
            <section className="ct">
                <div className="ct__container">
                    <div className="ct__hero">
                        <img src={ignisLogo} alt="Ignis" className="ct__hero-img" />
                        <h1 className="ct__title">
                            Meet <span className="ct__title-accent">Ignis</span>
                        </h1>
                        <p className="ct__subtitle">
                            Your AI-powered crypto trading engine. Ignis trades on your exchange account automatically — 
                            <span className="ct__subtitle-hl">trend detection</span>, 
                            <span className="ct__subtitle-hl">AI-reviewed entries</span>, 
                            <span className="ct__subtitle-hl">3-tier take profits</span>, and  
                            <span className="ct__subtitle-hl">smart risk management</span>.
                        </p>
                    </div>

                    {/* EXPIRED / DEACTIVATED USER BANNER */}
                    {(isExpired || isDeactivated) && (
                        <div className="ct__status-banner ct__status--inactive" style={{ marginBottom: '1.75rem' }}>
                            <Icon
                                icon="solar:restart-bold"
                                className="ct__status-icon"
                            />
                            <div className="ct__status-info">
                                <p className="ct__status-label">{isExpired ? 'Subscription Expired' : 'Subscription Inactive'}</p>
                                <p className="ct__status-detail">
                                    Re-enroll below to reactivate Ignis. You can keep the same exchange or switch — your call.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* 🎰 SLOT COUNTER */}
                    <div className="ct__slots-banner">
                        <div className="ct__slots-header">
                            <Icon icon="solar:users-group-rounded-bold" className="ct__slots-icon" />
                            <span className="ct__slots-label">Available Spots</span>
                        </div>
                        <div className="ct__slots-bar">
                            <div 
                                className="ct__slots-fill" 
                                style={{ width: `${(slotsTaken / MAX_SLOTS) * 100}%` }}
                            />
                        </div>
                        <div className="ct__slots-info">
                            <span className={`ct__slots-count ${slotsRemaining <= 3 ? 'ct__slots-count--urgent' : ''}`}>
                                {slotsFull ? 'All spots taken' : `${slotsRemaining} of ${MAX_SLOTS} spots remaining`}
                            </span>
                            {slotsRemaining > 0 && slotsRemaining <= 5 && (
                                <span className="ct__slots-warning">Filling fast</span>
                            )}
                        </div>
                    </div>

                    {/* 🔒 ENROLLMENT DISABLED (remote toggle) */}
                    {!enrollmentEnabled && (
                        <div className="ct__lock-banner">
                            <Icon icon="solar:lock-bold" className="ct__lock-icon" />
                            <div className="ct__lock-info">
                                <h3 className="ct__lock-title">{maintenanceMsg ? 'Enrollment Paused' : 'Coming Soon'}</h3>
                                <p className="ct__lock-text">
                                    {maintenanceMsg || 'Ignis is currently unavailable. Check back soon or join our Telegram for updates.'}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* 🔒 SLOTS FULL BANNER */}
                    {enrollmentEnabled && slotsFull && (
                        <div className="ct__lock-banner">
                            <Icon icon="solar:users-group-rounded-bold" className="ct__lock-icon" />
                            <div className="ct__lock-info">
                                <h3 className="ct__lock-title">All Spots Taken</h3>
                                <p className="ct__lock-text">
                                    All {MAX_SLOTS} spots are currently filled. Join the waitlist on Telegram for the next opening.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* TIER SELECTION — Always visible (read-only when locked) */}
                    <div className="ct__tiers">
                        <h2 className="ct__section-title">Choose Your Plan</h2>
                        <div className="ct__tier-grid">
                            {TIERS.map(tier => (
                                <button
                                    key={tier.id}
                                    className={`ct__tier-card ${selectedTier === tier.id ? 'ct__tier-card--selected' : ''}`}
                                    onClick={() => setSelectedTier(tier.id)}
                                    style={{ borderColor: selectedTier === tier.id ? tier.color : 'transparent' }}
                                >
                                    <div className="ct__tier-badge" style={{ background: tier.color }}>
                                        {tier.label}
                                    </div>
                                    <p className="ct__tier-price">${tier.price}<span>/month</span></p>
                                    <p className="ct__tier-cap">Up to {tier.cap} trading capital</p>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* API KEY FORM — Only when enrollment is open AND slots available */}
                    {enrollmentEnabled && !slotsFull && (
                    <div className="ct__enroll-form">
                        <h2 className="ct__section-title">Connect Your Exchange</h2>
                        
                        <div className="ct__exchange-row">
                            <button
                                className={`ct__exchange-badge ${selectedExchange === 'bybit' ? 'ct__exchange-active' : 'ct__exchange-inactive'}`}
                                onClick={() => setSelectedExchange('bybit')}
                            >
                                <Icon icon={selectedExchange === 'bybit' ? "solar:verified-check-bold" : "solar:circle-linear"} className="ct__exchange-icon" />
                                <span>Bybit</span>
                                <span className="ct__exchange-tag ct__tag-live">Live</span>
                            </button>
                            <button
                                className={`ct__exchange-badge ${selectedExchange === 'binance' ? 'ct__exchange-active' : 'ct__exchange-inactive'}`}
                                onClick={() => setSelectedExchange('binance')}
                            >
                                <Icon icon={selectedExchange === 'binance' ? "solar:verified-check-bold" : "solar:circle-linear"} className="ct__exchange-icon" />
                                <span>Binance</span>
                                <span className="ct__exchange-tag ct__tag-live">Live</span>
                            </button>
                            <button
                                className={`ct__exchange-badge ${selectedExchange === 'hyperliquid' ? 'ct__exchange-active' : 'ct__exchange-inactive'}`}
                                onClick={() => setSelectedExchange('hyperliquid')}
                            >
                                <Icon icon={selectedExchange === 'hyperliquid' ? "solar:verified-check-bold" : "solar:circle-linear"} className="ct__exchange-icon" />
                                <span>Hyperliquid</span>
                                <span className="ct__exchange-tag ct__tag-live">Live</span>
                            </button>
                        </div>

                        <div className="ct__assets-section">
                            <p className="ct__assets-label">Trading Pairs</p>
                            <div className="ct__assets-row">
                                <span className="ct__asset-chip">BTC</span>
                                <span className="ct__asset-chip">ETH</span>
                                <span className="ct__asset-chip">SOL</span>
                                <span className="ct__asset-chip">XRP</span>
                                <span className="ct__asset-chip ct__asset-chip--meme">DOGE</span>
                                <span className="ct__asset-chip ct__asset-chip--soon">More Coming Soon</span>
                            </div>
                        </div>
                        
                        <div className="ct__warning-box">
                            <Icon icon="solar:shield-check-linear" className="ct__warning-icon" />
                            <div>
                                <p className="ct__warning-title">Your Keys Are Safe With Us</p>
                                <p className="ct__warning-text">
                                    API keys are encrypted the moment you submit them and are <strong>never</strong> stored 
                                    in plain text. Ignis can only place trades on your behalf — it cannot withdraw or 
                                    transfer your funds under any circumstances.
                                </p>
                            </div>
                        </div>

                        <div className="ct__form-group">
                            <label className="ct__label">Your Trading Capital ({selectedExchange === 'hyperliquid' ? 'USDC' : 'USDT'})</label>
                            <input
                                type="number"
                                className="ct__input"
                                value={tradingCapital}
                                onChange={(e) => setTradingCapital(e.target.value)}
                                placeholder={`How much ${selectedExchange === 'hyperliquid' ? 'USDC' : 'USDT'} is in your ${selectedExchange === 'binance' ? 'Binance Futures' : selectedExchange === 'hyperliquid' ? 'Hyperliquid' : 'Bybit'} account? (max ${TIERS.find(t => t.id === selectedTier)?.cap})`}
                                min="100"
                                max={TIERS.find(t => t.id === selectedTier)?.cap?.replace(/[^0-9]/g, '') || 2000}
                                autoComplete="off"
                            />
                            <p className="ct__input-hint">
                                Ignis uses this to size your positions correctly. Must be between $100 and {TIERS.find(t => t.id === selectedTier)?.cap} for your tier.
                            </p>
                        </div>

                        <div className="ct__form-group">
                            <label className="ct__label">{selectedExchange === 'hyperliquid' ? 'Wallet Address' : selectedExchange === 'binance' ? 'Binance API Key' : 'Bybit API Key'}</label>
                            <input
                                type="text"
                                className="ct__input"
                                value={apiKey}
                                onChange={(e) => setApiKey(e.target.value)}
                                placeholder={selectedExchange === 'hyperliquid' ? 'Your wallet address (0x...)' : `Enter your ${selectedExchange === 'binance' ? 'Binance' : 'Bybit'} API key`}
                                autoComplete="off"
                            />
                        </div>

                        <div className="ct__form-group">
                            <label className="ct__label">{selectedExchange === 'hyperliquid' ? 'API Wallet Private Key' : selectedExchange === 'binance' ? 'Binance API Secret' : 'Bybit API Secret'}</label>
                            <div className="ct__input-row">
                                <input
                                    type={showSecret ? 'text' : 'password'}
                                    className="ct__input ct__input--flex"
                                    value={apiSecret}
                                    onChange={(e) => setApiSecret(e.target.value)}
                                    placeholder={selectedExchange === 'hyperliquid' ? 'Your API wallet private key (0x...)' : `Enter your ${selectedExchange === 'binance' ? 'Binance' : 'Bybit'} API secret`}
                                    autoComplete="off"
                                />
                                <button
                                    type="button"
                                    className="ct__eye-btn"
                                    onClick={() => setShowSecret(!showSecret)}
                                >
                                    <Icon icon={showSecret ? 'solar:eye-closed-linear' : 'solar:eye-linear'} />
                                </button>
                            </div>
                        </div>

                        {submitError && <p className="ct__error-text">{submitError}</p>}
                        {submitSuccess && <p className="ct__success-text">{submitSuccess}</p>}

                        <button
                            className="ct__cta-btn"
                            onClick={handleSubmitKeys}
                            disabled={submitting || !apiKey || !apiSecret || !tradingCapital}
                        >
                            {submitting ? 'Encrypting & Submitting...' : `Submit & Enroll — ${TIERS.find(t => t.id === selectedTier)?.label} ($${TIERS.find(t => t.id === selectedTier)?.price}/mo)`}
                        </button>

                        <div className="ct__steps">
                            <h3 className="ct__steps-title">Before You Start</h3>
                            <p className="ct__steps-subtitle">
                                Takes about {selectedExchange === 'hyperliquid' ? '3' : '5'} minutes. Set up your {selectedExchange === 'hyperliquid' ? 'Hyperliquid' : selectedExchange === 'binance' ? 'Binance' : 'Bybit'} account correctly and you're all set.
                            </p>
                            
                            {selectedExchange === 'hyperliquid' ? (
                                <>
                                    {/* HL STEP 1: Deposit USDC */}
                                    <div className="ct__step">
                                        <span className="ct__step-num">1</span>
                                        <div className="ct__step-content">
                                            <p className="ct__step-heading">Deposit USDC to Hyperliquid</p>
                                            <p className="ct__step-desc">
                                                Go to <strong><a href="https://app.hyperliquid.xyz" target="_blank" rel="noopener noreferrer" style={{color: '#c084fc'}}>app.hyperliquid.xyz</a></strong> and 
                                                connect your wallet. Click <strong>Deposit</strong> and send <strong>USDC</strong> from 
                                                Arbitrum to your Hyperliquid account. This is the capital Ignis will trade with.
                                            </p>
                                            <p className="ct__step-desc">
                                                Hyperliquid uses <strong>USDC</strong> (not USDT) for all perpetual futures trading.
                                            </p>
                                        </div>
                                    </div>

                                    {/* HL STEP 2: Generate API Wallet */}
                                    <div className="ct__step">
                                        <span className="ct__step-num">2</span>
                                        <div className="ct__step-content">
                                            <p className="ct__step-heading">Generate an API Wallet</p>
                                            <p className="ct__step-desc">
                                                Go to <strong><a href="https://app.hyperliquid.xyz/API" target="_blank" rel="noopener noreferrer" style={{color: '#c084fc'}}>app.hyperliquid.xyz/API</a></strong>. 
                                                Click <strong>"Generate API Private Key"</strong>. Give it a name like "Ignis Agent" and approve the transaction in your wallet.
                                            </p>
                                            <p className="ct__step-desc">
                                                You'll get a <strong>private key</strong> starting with <code>0x...</code> — <strong>save it immediately</strong>, 
                                                it's only shown once. This API wallet can trade on your behalf but <strong>cannot withdraw funds</strong>.
                                            </p>
                                            <div className="ct__checklist">
                                                <div className="ct__check-item ct__check-yes">
                                                    <Icon icon="solar:check-circle-bold" />
                                                    <div>
                                                        <span className="ct__check-label">Trading Permission</span>
                                                        <span className="ct__check-why">API wallet can open, close, and manage positions</span>
                                                    </div>
                                                </div>
                                                <div className="ct__check-divider">
                                                    <span>Built-in security</span>
                                                </div>
                                                <div className="ct__check-item ct__check-no">
                                                    <Icon icon="solar:close-circle-bold" />
                                                    <div>
                                                        <span className="ct__check-label">No Withdrawal Access</span>
                                                        <span className="ct__check-why">API wallets cannot move funds out of your account</span>
                                                    </div>
                                                </div>
                                                <div className="ct__check-item ct__check-no">
                                                    <Icon icon="solar:close-circle-bold" />
                                                    <div>
                                                        <span className="ct__check-label">No Transfer Access</span>
                                                        <span className="ct__check-why">Funds stay exactly where you put them</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <p className="ct__step-desc ct__step-note">
                                                <Icon icon="solar:info-circle-linear" /> No IP restriction needed — Hyperliquid API wallets are 
                                                secured by cryptographic signatures, not IP whitelists.
                                            </p>
                                        </div>
                                    </div>

                                    {/* HL STEP 3: Paste Keys */}
                                    <div className="ct__step">
                                        <span className="ct__step-num">3</span>
                                        <div className="ct__step-content">
                                            <p className="ct__step-heading">Paste Your Wallet Address & API Key</p>
                                            <p className="ct__step-desc">
                                                In the <strong>Wallet Address</strong> field above, paste your <strong>main wallet address</strong> (the one 
                                                you connected to Hyperliquid — starts with <code>0x</code>). In the <strong>API Wallet Private Key</strong> field, 
                                                paste the private key you just generated. Pick your tier and hit submit.
                                            </p>
                                            <div className="ct__warning-banner">
                                                <Icon icon="solar:danger-triangle-bold" />
                                                <span>Use your <strong>main wallet address</strong> (not the API wallet address) in the first field.</span>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                                        {/* STEP 1: Move Funds */}
                            <div className="ct__step">
                                <span className="ct__step-num">1</span>
                                <div className="ct__step-content">
                                    <p className="ct__step-heading">
                                        {selectedExchange === 'binance' ? 'Move Your Funds to Futures Wallet' : 'Move Your Funds to Unified Trading'}
                                    </p>
                                    <p className="ct__step-desc">
                                        {selectedExchange === 'binance' ? (
                                            <>Ignis places trades through Binance's <strong>USDT-M Futures</strong> account.
                                            If your USDT is in Spot, go to <strong>Wallet → Transfer</strong> and move it from 
                                            <strong> Spot</strong> to <strong>USDT-M Futures</strong> — otherwise Ignis will connect but 
                                            won't be able to open any positions.</>
                                        ) : (
                                            <>Ignis places trades through Bybit's <strong>Unified Trading</strong> account.
                                            If your balance is sitting in Funding or Spot, hit <em>Transfer</em> in Bybit 
                                            and move it over — otherwise Ignis will connect but won't be able 
                                            to open any positions.</>
                                        )}
                                    </p>
                                </div>
                            </div>

                            {/* STEP 2: Isolated Margin */}
                            <div className="ct__step">
                                <span className="ct__step-num">2</span>
                                <div className="ct__step-content">
                                    <p className="ct__step-heading">Switch to Isolated Margin</p>
                                    <div className="ct__warning-banner">
                                        <Icon icon="solar:danger-triangle-bold" />
                                        <span>This step is <strong>mandatory</strong> — we won't activate accounts using Cross margin.</span>
                                    </div>
                                    <p className="ct__step-desc">
                                        {selectedExchange === 'binance' ? (
                                            <>Go to <strong>Futures → USDT-M</strong>. On any trading pair, click the 
                                            margin mode button (it may say "Cross") and switch to <strong>Isolated</strong>. 
                                            With Cross margin, one bad trade can liquidate your entire balance. 
                                            Isolated limits each position to only risk its own margin.</>
                                        ) : (
                                            <>Go to any trading pair → click the margin mode dropdown → select <strong>Isolated</strong>. 
                                            With Cross margin, one bad trade can liquidate your entire balance. 
                                            Isolated limits each position to only risk its own margin — so even in 
                                            extreme moves, the damage stays contained to that single trade.</>
                                        )}
                                    </p>
                                </div>
                            </div>

                            {/* STEP 3: Leverage */}
                            <div className="ct__step">
                                <span className="ct__step-num">3</span>
                                <div className="ct__step-content">
                                    <p className="ct__step-heading">Set Leverage to 1×</p>
                                    <p className="ct__step-desc">
                                        {selectedExchange === 'binance' ? (
                                            <>In Binance, go to <strong>Futures → USDT-M</strong>. You'll see the leverage 
                                            button near the order panel (e.g. "20x"). Click it and <strong>set it to 1×</strong>. 
                                            Ignis manages its own position sizing — starting at 1× keeps your account safe 
                                            while Ignis handles the rest.</>
                                        ) : (
                                            <>In Bybit, tap <strong>Trade</strong> at the bottom, then select <strong>Futures</strong>. 
                                            You'll see a leverage button (e.g. "10x") near the top. Tap it and <strong>set it to 1×</strong>. 
                                            If Bybit shows a confirmation or agreement popup, tap <strong>Agree</strong>. 
                                            Ignis manages its own position sizing — starting at 1× keeps your account safe 
                                            while Ignis handles the rest.</>
                                        )}
                                    </p>
                                    <div className="ct__warning-banner">
                                        <Icon icon="solar:danger-triangle-bold" />
                                        <span>Higher leverage = higher liquidation risk. <strong>1× is required</strong> before activation.</span>
                                    </div>
                                </div>
                            </div>

                            {/* STEP 4: Hedge Mode */}
                            <div className="ct__step">
                                <span className="ct__step-num">4</span>
                                <div className="ct__step-content">
                                    <p className="ct__step-heading">Enable Hedge Mode</p>
                                    <p className="ct__step-desc">
                                        {selectedExchange === 'binance' ? (
                                            <>Go to <strong>Futures → USDT-M</strong>. Click the <strong>⚙️ Settings</strong> icon 
                                            (top-right of the trading page). Find <strong>Position Mode</strong> and switch 
                                            from <strong>One-Way</strong> to <strong>Hedge Mode</strong>.</>
                                        ) : (
                                            <>Go to <strong>Trade → Futures</strong> in Bybit. Tap the <strong>⋯</strong> (three dots) menu 
                                            and select <strong>Trading Preferences</strong>. 
                                            Find <strong>Position Mode</strong> and switch it to <strong>Hedge Mode</strong>. 
                                            Then check the box that says <strong>"Apply to all USDT-Perp pairs"</strong> and tap <strong>OK</strong>.</>
                                        )}
                                    </p>
                                    <p className="ct__step-desc">
                                        Hedge mode lets Ignis hold both long and short positions at the same time — 
                                        this is essential for how Ignis manages risk across multiple strategies. 
                                        Without it, Ignis won't be able to open positions correctly.
                                    </p>
                                    <div className="ct__warning-banner">
                                        <Icon icon="solar:info-circle-bold" />
                                        <span>You can't switch modes while holding open positions or active orders.</span>
                                    </div>
                                </div>
                            </div>

                            {/* STEP 5: API Key */}
                            <div className="ct__step">
                                <span className="ct__step-num">5</span>
                                <div className="ct__step-content">
                                    <p className="ct__step-heading">Create Your API Key</p>
                                    {selectedExchange === 'binance' ? (
                                        <>
                                            <p className="ct__step-desc">
                                                Go to <strong>Binance → Account → API Management</strong> (or search "API" in settings). 
                                                Click <strong>Create API</strong> and choose <strong>System generated</strong>. 
                                                After verifying, configure these permissions:
                                            </p>
                                            <div className="ct__checklist">
                                                <div className="ct__check-item ct__check-yes">
                                                    <Icon icon="solar:check-circle-bold" />
                                                    <div>
                                                        <span className="ct__check-label">Enable Futures</span>
                                                        <span className="ct__check-why">Lets the bot trade USDT-M perpetual futures</span>
                                                    </div>
                                                </div>
                                                <div className="ct__check-item ct__check-yes">
                                                    <Icon icon="solar:check-circle-bold" />
                                                    <div>
                                                        <span className="ct__check-label">Read Info</span>
                                                        <span className="ct__check-why">Lets the bot check balances and positions</span>
                                                    </div>
                                                </div>
                                                <div className="ct__check-divider">
                                                    <span>Leave everything else unchecked</span>
                                                </div>
                                                <div className="ct__check-item ct__check-no">
                                                    <Icon icon="solar:close-circle-bold" />
                                                    <div>
                                                        <span className="ct__check-label">Enable Withdrawals</span>
                                                        <span className="ct__check-why">Nobody can move funds out of your account</span>
                                                    </div>
                                                </div>
                                                <div className="ct__check-item ct__check-no">
                                                    <Icon icon="solar:close-circle-bold" />
                                                    <div>
                                                        <span className="ct__check-label">Internal Transfer</span>
                                                        <span className="ct__check-why">Funds stay exactly where you put them</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <p className="ct__step-desc">
                                                Head to <strong>Bybit → Account & Security → API Management → Create New Key</strong>. 
                                                Choose <em>System-generated API Keys</em>. On the permissions screen, 
                                                you only need to check <strong>two boxes</strong>:
                                            </p>
                                            <div className="ct__checklist">
                                                <div className="ct__check-item ct__check-yes">
                                                    <Icon icon="solar:check-circle-bold" />
                                                    <div>
                                                        <span className="ct__check-label">Read & Write</span>
                                                        <span className="ct__check-why">Lets the bot place and manage trades</span>
                                                    </div>
                                                </div>
                                                <div className="ct__check-item ct__check-yes">
                                                    <Icon icon="solar:check-circle-bold" />
                                                    <div>
                                                        <span className="ct__check-label">Unified Trading</span>
                                                        <span className="ct__check-why">Grants access to your derivatives account</span>
                                                    </div>
                                                </div>
                                                <div className="ct__check-divider">
                                                    <span>Leave everything else unchecked</span>
                                                </div>
                                                <div className="ct__check-item ct__check-no">
                                                    <Icon icon="solar:close-circle-bold" />
                                                    <div>
                                                        <span className="ct__check-label">Withdrawal</span>
                                                        <span className="ct__check-why">Nobody can move funds out of your account</span>
                                                    </div>
                                                </div>
                                                <div className="ct__check-item ct__check-no">
                                                    <Icon icon="solar:close-circle-bold" />
                                                    <div>
                                                        <span className="ct__check-label">Asset Transfer</span>
                                                        <span className="ct__check-why">Funds stay exactly where you put them</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    {selectedExchange === 'binance' ? (
                                        <p className="ct__step-desc ct__step-note">
                                            <Icon icon="solar:info-circle-linear" /> For IP restriction, select <strong>"Restrict access to trusted IPs only"</strong> and 
                                            add our server IP: <strong style={{ color: '#c084fc', fontFamily: "'JetBrains Mono', monospace" }}>167.179.98.201</strong>. 
                                            Binance requires an IP whitelist before you can enable Futures.
                                        </p>
                                    ) : (
                                        <p className="ct__step-desc ct__step-note">
                                            <Icon icon="solar:info-circle-linear" /> For IP restriction, select <strong>"No restriction"</strong> — 
                                            our trading servers rotate IPs and a whitelist would block connections.
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* STEP 6: Paste Keys */}
                            <div className="ct__step">
                                <span className="ct__step-num">6</span>
                                <div className="ct__step-content">
                                    <p className="ct__step-heading">Paste Your Keys & Pick Your Tier</p>
                                    <p className="ct__step-desc">
                                        Copy both the API key and secret that {selectedExchange === 'binance' ? 'Binance' : 'Bybit'} gives you (the secret is only shown once, 
                                        so save it). Paste them in the fields above, choose the plan that fits your capital, 
                                        and hit submit.
                                    </p>
                                </div>
                            </div>


                                </>
                            )}

                                                        {/* STEP 7: Payment */}
                            <div className="ct__step">
                                <span className="ct__step-num">7</span>
                                <div className="ct__step-content">
                                    <p className="ct__step-heading">Send Payment & Go Live</p>
                                    <p className="ct__step-desc">
                                        After submitting, you'll see a USDT wallet address. Send your first month's 
                                        subscription there in USDT (all tiers pay in USDT regardless of exchange). Once confirmed, we activate Ignis and it starts mirroring 
                                        trades from both our AI engines — usually within a few hours. 
                                        You'll get a Telegram alert when it's live.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    )}
                </div>
            </section>
        );
    }

    // ==========================================
    // ENROLLED — Show dashboard
    // ==========================================
    const isActive = botStatus.active;
    const isPending = botStatus.pendingActivation;
    const tierInfo = TIERS.find(t => t.id === botStatus.tier) || TIERS[0];

    // Performance calculations — use backend stats (computed from ALL trades, not just displayed)
    const winRate = botStatus.totalTrades > 0
        ? ((botStatus.wins / botStatus.totalTrades) * 100).toFixed(1)
        : '0.0';
    const totalPnl = botStatus.totalPnl ?? 0;
    const activeCount = (botStatus.activeTrades || []).length;

    return (
        <section className="ct">
            <div className="ct__container">
                <div className="ct__hero ct__hero--compact">
                    <h1 className="ct__title">
                        <span className="ct__title-accent">Ignis</span> Dashboard
                    </h1>
                    <p className="ct__subtitle">
                        {isActive 
                            ? `${tierInfo.label} Plan · Ignis is managing your trades`
                            : 'Ignis Auto-Trading'
                        }
                    </p>
                </div>

                {/* STATUS BANNER */}
                <div className={`ct__status-banner ${isActive ? 'ct__status--active' : isPending ? 'ct__status--pending' : 'ct__status--inactive'}`}>
                    <Icon
                        icon={isActive ? 'solar:check-circle-bold' : isPending ? 'solar:clock-circle-bold' : 'solar:close-circle-bold'}
                        className="ct__status-icon"
                    />
                    <div className="ct__status-info">
                        <p className="ct__status-label">
                            {isActive ? 'Ignis Active — Trading 24/7' : isPending ? 'Pending Payment' : 'Subscription Expired'}
                        </p>
                        <p className="ct__status-detail">
                            {isActive
                                ? `Up to ${tierInfo.cap} trading capital · Telegram alerts enabled`
                                : isPending
                                ? `Send $${tierInfo.price} USDT to activate your ${tierInfo.label} plan`
                                : 'Renew your subscription to resume trading'
                            }
                        </p>
                    </div>
                </div>

                {/* TELEGRAM GROUP LINK — Active users */}
                {isActive && (
                    <a href={TELEGRAM_GROUP_LINK} target="_blank" rel="noopener noreferrer" className="ct__telegram-card">
                        <Icon icon="logos:telegram" className="ct__telegram-icon" />
                        <div className="ct__telegram-info">
                            <p className="ct__telegram-title">Join the Trading Alerts Group</p>
                            <p className="ct__telegram-text">Get real-time trade notifications, bot alerts, and community chat</p>
                        </div>
                        <Icon icon="solar:arrow-right-linear" className="ct__telegram-arrow" />
                    </a>
                )}

                {/* PAYMENT INFO — Show when pending */}
                {isPending && (
                    <div className="ct__payment-box">
                        <h3 className="ct__payment-title">
                            <Icon icon="solar:wallet-linear" /> Payment Details
                        </h3>
                        <div className="ct__payment-row">
                            <span className="ct__payment-label">Send exactly:</span>
                            <span className="ct__payment-value">${tierInfo.price} USDT</span>
                        </div>
                        <div className="ct__payment-row">
                            <span className="ct__payment-label">Network:</span>
                            <span className="ct__payment-value">{PAYMENT_NETWORK}</span>
                        </div>
                        <div className="ct__payment-row">
                            <span className="ct__payment-label">Wallet:</span>
                            <span className="ct__payment-value ct__payment-wallet">{PAYMENT_WALLET}</span>
                        </div>
                        <button
                            className="ct__copy-btn"
                            onClick={() => {
                                navigator.clipboard.writeText(PAYMENT_WALLET);
                            }}
                        >
                            <Icon icon="solar:copy-linear" /> Copy Address
                        </button>

                        {/* TX HASH VERIFICATION */}
                        <div className="ct__verify-section">
                            <h4 className="ct__verify-title">
                                <Icon icon="solar:shield-check-bold" /> Verify Your Payment
                            </h4>
                            <p className="ct__verify-desc">
                                After sending USDT, paste your transaction hash below to instantly activate your subscription.
                            </p>
                            <div className="ct__verify-input-row">
                                <input
                                    type="text"
                                    className="ct__verify-input"
                                    placeholder="Paste your TRC-20 transaction hash..."
                                    value={txHash}
                                    onChange={(e) => {
                                        setTxHash(e.target.value);
                                        setVerifyError('');
                                        setVerifySuccess('');
                                    }}
                                    disabled={verifying}
                                    spellCheck={false}
                                    autoComplete="off"
                                />
                                <button
                                    className="ct__verify-btn"
                                    onClick={handleVerifyPayment}
                                    disabled={verifying || !txHash.trim()}
                                >
                                    {verifying ? (
                                        <>
                                            <Icon icon="solar:refresh-bold" className="ct__spin" /> Verifying...
                                        </>
                                    ) : (
                                        <>
                                            <Icon icon="solar:shield-check-bold" /> Verify Payment
                                        </>
                                    )}
                                </button>
                            </div>
                            {verifyError && (
                                <p className="ct__verify-error">
                                    <Icon icon="solar:close-circle-bold" /> {verifyError}
                                </p>
                            )}
                            {verifySuccess && (
                                <p className="ct__verify-success">
                                    <Icon icon="solar:check-circle-bold" /> {verifySuccess}
                                </p>
                            )}
                            <p className="ct__verify-help">
                                Don't know your TX hash? Open your Tron wallet app, find the transaction, and copy the Transaction ID / Hash.
                            </p>
                        </div>
                    </div>
                )}

                {/* DASHBOARD TABS */}
                {isActive && (
                    <>
                        <div className="ct__dash-tabs">
                            <button
                                className={`ct__subtab ${dashTab === 'overview' ? 'ct__subtab--active' : ''}`}
                                onClick={() => setDashTab('overview')}
                            >
                                Overview
                            </button>
                            <button
                                className={`ct__subtab ${dashTab === 'trades' ? 'ct__subtab--active' : ''}`}
                                onClick={() => setDashTab('trades')}
                            >
                                Active Trades
                            </button>
                            <button
                                className={`ct__subtab ${dashTab === 'history' ? 'ct__subtab--active' : ''}`}
                                onClick={() => setDashTab('history')}
                            >
                                History
                            </button>
                            <button
                                className={`ct__subtab ${dashTab === 'settings' ? 'ct__subtab--active' : ''}`}
                                onClick={() => setDashTab('settings')}
                            >
                                Settings
                            </button>
                        </div>

                        {/* OVERVIEW TAB */}
                        {dashTab === 'overview' && (
                            <div className="ct__overview">
                                <div className="ct__stat-grid">
                                    <div className="ct__stat-card">
                                        <Icon icon="solar:chart-2-bold" className="ct__stat-icon" style={{ color: '#3b82f6' }} />
                                        <p className="ct__stat-label">Win Rate</p>
                                        <p className="ct__stat-value">{winRate}%</p>
                                    </div>
                                    <div className="ct__stat-card">
                                        <Icon icon="solar:dollar-bold" className="ct__stat-icon" style={{ color: totalPnl >= 0 ? '#4ade80' : '#f87171' }} />
                                        <p className="ct__stat-label">Total PnL</p>
                                        <p className={`ct__stat-value ${totalPnl >= 0 ? 'ct__stat--green' : 'ct__stat--red'}`}>
                                            {totalPnl >= 0 ? '+' : ''}{totalPnl.toFixed(2)}%
                                        </p>
                                    </div>
                                    <div className="ct__stat-card">
                                        <Icon icon="solar:history-bold" className="ct__stat-icon" style={{ color: '#a78bfa' }} />
                                        <p className="ct__stat-label">Total Trades</p>
                                        <p className="ct__stat-value">{botStatus.totalTrades || 0}</p>
                                        <p className="ct__stat-sub">{botStatus.wins || 0}W / {botStatus.losses || 0}L</p>
                                    </div>
                                    <div className="ct__stat-card">
                                        <Icon icon="solar:play-circle-bold" className="ct__stat-icon" style={{ color: '#fbbf24' }} />
                                        <p className="ct__stat-label">Active Now</p>
                                        <p className="ct__stat-value">{activeCount}</p>
                                    </div>
                                </div>

                                {/* Active positions quick view */}
                                {(botStatus.activeTrades || []).length > 0 && (
                                    <div className="ct__active-quick">
                                        <h3 className="ct__section-title">Current Positions</h3>
                                        {botStatus.activeTrades.map(trade => (
                                            <div key={trade.id} className="ct__trade-row">
                                                <span className={`ct__trade-dir ${trade.direction === 'LONG' ? 'ct__dir--long' : 'ct__dir--short'}`}>
                                                    {trade.direction === 'LONG' ? '↑' : '↓'} {trade.asset}
                                                </span>
                                                <span className="ct__trade-tf">{trade.timeframe}</span>
                                                <span className="ct__trade-entry">${trade.entryPrice?.toFixed(2)}</span>
                                                <span className={`ct__trade-pnl ${(trade.pnl || 0) >= 0 ? 'ct__stat--green' : 'ct__stat--red'}`}>
                                                    {(trade.pnl || 0) >= 0 ? '+' : ''}{(trade.pnl || 0).toFixed(2)}%
                                                </span>
                                                <span className="ct__trade-status">{trade.status}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ACTIVE TRADES TAB */}
                        {dashTab === 'trades' && (
                            <div className="ct__trades-tab">
                                {(botStatus.activeTrades || []).length === 0 ? (
                                    <div className="ct__empty-state">
                                        <Icon icon="solar:radar-2-bold" className="ct__empty-icon" />
                                        <p className="ct__empty">No active trades right now</p>
                                        <p className="ct__empty-sub">Bot is scanning for high-probability setups...</p>
                                    </div>
                                ) : (
                                    botStatus.activeTrades.map(trade => (
                                        <div key={trade.id} className="ct__trade-card">
                                            <div className="ct__trade-header">
                                                <div className="ct__trade-pair">
                                                    <span className={`ct__trade-dir-badge ${trade.direction === 'LONG' ? 'ct__dir--long' : 'ct__dir--short'}`}>
                                                        {trade.direction === 'LONG' ? '↑ LONG' : '↓ SHORT'}
                                                    </span>
                                                    <span className="ct__trade-asset">{trade.asset}</span>
                                                </div>
                                                <span className="ct__trade-tf">{trade.timeframe}</span>
                                            </div>
                                            <div className="ct__trade-levels">
                                                <div className="ct__trade-level-row">
                                                    <span className="ct__level-label">Entry</span>
                                                    <span className="ct__level-value">${trade.entryPrice?.toFixed(2)}</span>
                                                </div>
                                                <div className="ct__trade-level-row">
                                                    <span className="ct__level-label ct__level--red">Stop Loss</span>
                                                    <span className="ct__level-value">${trade.stopLoss?.toFixed(2)}</span>
                                                </div>
                                                <div className="ct__trade-level-row">
                                                    <span className="ct__level-label ct__level--green">TP1 → TP2 → TP3</span>
                                                    <span className="ct__level-value">${trade.tp1?.toFixed(2)} → ${trade.tp2?.toFixed(2)} → ${trade.tp3?.toFixed(2)}</span>
                                                </div>
                                            </div>
                                            <div className="ct__trade-footer">
                                                <span className="ct__trade-status-badge">{trade.status}</span>
                                                <span className={`ct__trade-pnl ${(trade.pnl || 0) >= 0 ? 'ct__stat--green' : 'ct__stat--red'}`}>
                                                    {(trade.pnl || 0) >= 0 ? '+' : ''}{(trade.pnl || 0).toFixed(2)}%
                                                </span>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}

                        {/* HISTORY TAB */}
                        {dashTab === 'history' && (
                            <div className="ct__history-tab">
                                {(botStatus.recentTrades || []).length === 0 ? (
                                    <div className="ct__empty-state">
                                        <Icon icon="solar:document-text-bold" className="ct__empty-icon" />
                                        <p className="ct__empty">No trade history yet</p>
                                        <p className="ct__empty-sub">Completed trades will appear here</p>
                                    </div>
                                ) : (
                                    <div className="ct__history-list">
                                        {botStatus.recentTrades.map(trade => {
                                            const pnl = trade.pnl || 0;
                                            // Centralized classifier — must match backend classifyTrade exactly
                                            const outcome = trade.outcome || '';
                                            const classification = (outcome === 'WIN' || trade.status === 'FULL_TP' || ((!outcome || outcome === 'UNKNOWN') && pnl > 0.1)) ? 'WIN'
                                                : (outcome === 'LOSS' || ((!outcome || outcome === 'UNKNOWN') && pnl < -0.1)) ? 'LOSS'
                                                : 'BREAKEVEN';
                                            const isWin = classification === 'WIN';
                                            const isLoss = classification === 'LOSS';
                                            const isBreakeven = classification === 'BREAKEVEN';
                                            const outcomeLabel = isWin ? 'WIN' : isLoss ? 'LOSS' : 'BREAKEVEN';
                                            const outcomeIcon = isWin ? '🎯' 
                                                : isLoss ? '🛑'
                                                : trade.status === 'EMERGENCY_CLOSE' ? '🚨'
                                                : '⚪';
                                            const reasonLabel = trade.status === 'FULL_TP' ? 'Full TP'
                                                : trade.status === 'STOP_LOSS' || trade.status === 'STOPPED' ? 'Stopped'
                                                : trade.status === 'STOPPED_BREAKEVEN' || trade.status === 'BREAKEVEN' ? 'Breakeven'
                                                : trade.status === 'PARTIAL_TP' ? 'Partial TP'
                                                : trade.status === 'SIGNAL_CLOSE' ? 'Signal Close'
                                                : trade.status === 'TRAILING_STOP' ? 'Trail Stop'
                                                : trade.status === 'POSITION_GONE' ? 'Exchange Close'
                                                : trade.status === 'EMERGENCY_CLOSE' ? 'Emergency Close'
                                                : trade.status || 'Closed';
                                            // Robust date parsing — handles all formats from Cloud Functions
                                            const parseTradeDate = (trade) => {
                                                // Priority 1: closedAtMs (number in milliseconds — what backend stores)
                                                if (trade.closedAtMs && typeof trade.closedAtMs === 'number') {
                                                    return new Date(trade.closedAtMs);
                                                }
                                                // Priority 2: closedAt as Firestore Timestamp (has .toDate method)
                                                if (trade.closedAt?.toDate) {
                                                    return trade.closedAt.toDate();
                                                }
                                                // Priority 3: closedAt as serialized Firestore Timestamp {_seconds, _nanoseconds}
                                                if (trade.closedAt?._seconds) {
                                                    return new Date(trade.closedAt._seconds * 1000);
                                                }
                                                // Priority 4: closedAt as number (ms or seconds)
                                                if (trade.closedAt && typeof trade.closedAt === 'number') {
                                                    return new Date(trade.closedAt > 9999999999 ? trade.closedAt : trade.closedAt * 1000);
                                                }
                                                // Priority 5: closedAt as ISO string
                                                if (trade.closedAt && typeof trade.closedAt === 'string') {
                                                    return new Date(trade.closedAt);
                                                }
                                                // Priority 6: updatedAt fallbacks (same chain)
                                                if (trade.updatedAt?.toDate) return trade.updatedAt.toDate();
                                                if (trade.updatedAt?._seconds) return new Date(trade.updatedAt._seconds * 1000);
                                                if (trade.updatedAt) return new Date(trade.updatedAt);
                                                return null;
                                            };
                                            const closedDate = parseTradeDate(trade);
                                            const dateStr = closedDate && !isNaN(closedDate.getTime())
                                                ? closedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                                                : '';
                                            return (
                                                <div key={trade.id} className="ct__history-card">
                                                    <div className="ct__history-top">
                                                        <span className="ct__history-asset-info">
                                                            <span className={`ct__trade-dir-badge ${trade.direction === 'LONG' ? 'ct__dir--long' : 'ct__dir--short'}`}>
                                                                {trade.direction === 'LONG' ? '↑' : '↓'}
                                                            </span>
                                                            <span className="ct__history-asset">{trade.asset}</span>
                                                            <span className="ct__history-dir-label">{trade.direction}</span>
                                                            {trade.timeframe && <span className="ct__history-tf">{trade.timeframe}</span>}
                                                        </span>
                                                        <span className={`ct__history-outcome ${isWin ? 'ct__outcome--win' : isLoss ? 'ct__outcome--loss' : 'ct__outcome--be'}`}>
                                                            {pnl >= 0 ? '+' : ''}{pnl.toFixed(2)}%
                                                        </span>
                                                    </div>
                                                    <div className="ct__history-details">
                                                        <span className="ct__history-reason">{outcomeIcon} {reasonLabel}</span>
                                                        <span className={`ct__history-outcome-label ${isWin ? 'ct__outcome--win' : isLoss ? 'ct__outcome--loss' : 'ct__outcome--be'}`}>
                                                            {outcomeLabel}
                                                        </span>
                                                    </div>
                                                    <div className="ct__history-meta">
                                                        {trade.entryPrice ? (
                                                            <span className="ct__history-price">Entry: ${Number(trade.entryPrice).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 4})}</span>
                                                        ) : null}
                                                        {trade.exitPrice ? (
                                                            <span className="ct__history-price">Exit: ${Number(trade.exitPrice).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 4})}</span>
                                                        ) : null}
                                                        {dateStr && <span className="ct__history-date">{dateStr}</span>}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* SETTINGS TAB */}
                        {dashTab === 'settings' && (
                            <div className="ct__settings-tab">
                                <div className="ct__setting-group">
                                    <h3 className="ct__section-title">Subscription</h3>
                                    <p className="ct__setting-detail">
                                        Plan: <strong>{tierInfo.label}</strong> — 
                                        Trading Capital: <strong>${botStatus.tradingCapital?.toLocaleString() || tierInfo.cap}</strong> —
                                        ${tierInfo.price}/month
                                    </p>
                                </div>

                                <div className="ct__setting-group">
                                    <h3 className="ct__section-title">API Keys</h3>
                                    <p className="ct__setting-detail">
                                        Your keys are encrypted and stored securely. To change them:
                                    </p>
                                    
                                    {!editingKeys ? (
                                        <button className="ct__btn-outline" onClick={() => setEditingKeys(true)}>
                                            <Icon icon="solar:pen-linear" /> Update API Keys
                                        </button>
                                    ) : (
                                        <div className="ct__key-edit">
                                            <div className="ct__form-group">
                                                <label className="ct__label">New API Key</label>
                                                <input
                                                    type="text"
                                                    className="ct__input"
                                                    value={newApiKey}
                                                    onChange={(e) => setNewApiKey(e.target.value)}
                                                    placeholder={`Enter new ${botStatus?.exchange === 'binance' ? 'Binance' : 'Bybit'} API key`}
                                                    autoComplete="off"
                                                />
                                            </div>
                                            <div className="ct__form-group">
                                                <label className="ct__label">New API Secret</label>
                                                <input
                                                    type="password"
                                                    className="ct__input"
                                                    value={newApiSecret}
                                                    onChange={(e) => setNewApiSecret(e.target.value)}
                                                    placeholder={`Enter new ${botStatus?.exchange === 'binance' ? 'Binance' : 'Bybit'} API secret`}
                                                    autoComplete="off"
                                                />
                                            </div>
                                            {updateError && <p className="ct__error-text">{updateError}</p>}
                                            {updateSuccess && <p className="ct__success-text">{updateSuccess}</p>}
                                            <div className="ct__btn-row">
                                                <button className="ct__cta-btn" onClick={handleUpdateKeys} disabled={updating}>
                                                    {updating ? 'Updating...' : 'Save New Keys'}
                                                </button>
                                                <button className="ct__btn-outline" onClick={() => { setEditingKeys(false); setUpdateError(''); }}>
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="ct__setting-group">
                                    <h3 className="ct__section-title">Keep Your Account Safe</h3>
                                    <p className="ct__setting-detail ct__setting-subtitle">
                                        A few things to double-check so Ignis runs smoothly and your funds stay protected.
                                    </p>
                                    
                                    <div className="ct__safety-card ct__safety-critical">
                                        <Icon icon="solar:danger-triangle-bold" className="ct__safety-icon" />
                                        <div>
                                            <p className="ct__safety-heading">Isolated Margin — Not Cross</p>
                                            <p className="ct__safety-desc">
                                                This is the single most important setting. With Cross margin, one bad move 
                                                can wipe your entire balance — it happened on Oct 10th when stop-losses 
                                                and TPs failed across exchanges. <strong>Isolated</strong> caps each trade's 
                                                risk to its own margin. We verify this before activating any account.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="ct__safety-card ct__safety-warning">
                                        <Icon icon="solar:wallet-money-bold" className="ct__safety-icon" />
                                        <div>
                                            <p className="ct__safety-heading">Funds in {botStatus?.exchange === 'binance' ? 'Futures Wallet' : 'Unified Trading'}</p>
                                            <p className="ct__safety-desc">
                                                {botStatus?.exchange === 'binance' ? (
                                                    <>Ignis trades through Binance's USDT-M Futures wallet. If your balance 
                                                    is in Spot, use Binance's <em>Transfer</em> button to move it 
                                                    to USDT-M Futures. Takes 2 seconds — but Ignis can't open positions without it.</>
                                                ) : (
                                                    <>Ignis trades through Bybit's Unified Trading account. If your balance 
                                                    is in Funding or Spot, use Bybit's <em>Transfer</em> button to move it 
                                                    over. Takes 2 seconds — but Ignis can't open positions without it.</>
                                                )}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="ct__safety-card">
                                        <Icon icon="solar:lock-keyhole-bold" className="ct__safety-icon" />
                                        <div>
                                            <p className="ct__safety-heading">API: Read & Write + Unified Trading Only</p>
                                            <p className="ct__safety-desc">
                                                Your key should have exactly two permissions checked. Leave Withdrawal 
                                                and Asset Transfer off — Ignis never needs them, and it means 
                                                nobody (including us) can move funds out of your account.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="ct__safety-card">
                                        <Icon icon="solar:hand-shake-bold" className="ct__safety-icon" />
                                        <div>
                                            <p className="ct__safety-heading">Avoid Manual Trades on Bot Assets</p>
                                            <p className="ct__safety-desc">
                                                Ignis manages BTC, ETH, SOL, XRP, and DOGE. Opening your own positions 
                                                on the same pairs can conflict with Ignis's stops and take-profits. 
                                                You're free to trade any other pairs manually — just steer clear of these five 
                                                while Ignis is running.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="ct__safety-card">
                                        <Icon icon="solar:arrow-up-bold" className="ct__safety-icon" />
                                        <div>
                                            <p className="ct__safety-heading">Want to Change Tiers?</p>
                                            <p className="ct__safety-desc">
                                                Reach out on Telegram and we'll adjust your capital cap. 
                                                Upgrades and downgrades take effect at the start of your next billing cycle — no 
                                                need to re-submit your API keys.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* Refresh button */}
                <div className="ct__refresh-row">
                    <button className="ct__btn-outline" onClick={fetchStatus} disabled={statusLoading}>
                        <Icon icon="solar:refresh-linear" /> {statusLoading ? 'Loading...' : 'Refresh'}
                    </button>
                </div>
            </div>
        </section>
    );
};

export default CryptoTrades;